/**
 * ObservabilityPage: admin observability dashboard.
 *
 * Tabs (kept in the URL):
 *   - Anomalies: cross-daemon findings feed (excludes promotion_candidate)
 *   - Promotion candidates: the review list from the Promotion candidates page
 *
 * Restart loops are not yet a separate persistence stream; they surface in the anomaly feed once the
 * audit-overseer's restart-loop findings are written to `daemon_findings`.
 */
import { useState } from 'react';
import { Activity, TrendingUp } from 'lucide-react';
import { FilterPills } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/DetailPage.js';
import { AnomalyFeed } from '../../components/observability/AnomalyFeed.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { PAGE_HELP } from '../../help/index.js';
import { PromotionCandidatesList } from './PromotionCandidatesList.js';

const TAB_IDS = ['anomalies', 'promotions'] as const;
const TABS = [
  { id: 'anomalies', label: 'Anomalies', icon: Activity },
  { id: 'promotions', label: 'Promotion candidates', icon: TrendingUp },
];
const SCOPE_PILLS = [
  { id: 'pending', label: 'Pending review' },
  { id: 'all', label: 'All' },
];

export function ObservabilityPage() {
  const [tab, setTab] = useTabParam(TAB_IDS, 'anomalies');
  const [scope, setScope] = useState('pending');

  return (
    <DetailPage
      title="Observability"
      subtitle="What the platform's daemons have noticed across every organization."
      helpContent={PAGE_HELP.observability}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === 'anomalies' && <AnomalyFeed />}
      {tab === 'promotions' && (
        <div className="space-y-4">
          <FilterPills aria-label="Show" options={SCOPE_PILLS} value={scope} onChange={setScope} />
          <PromotionCandidatesList showAll={scope === 'all'} framed />
        </div>
      )}
    </DetailPage>
  );
}
